import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { NAV_FLAT } from '@/config/navigation';

// Cmd+K / Ctrl+K jump-to-feature palette. Reads the same NAV_FLAT list as
// Sidebar.js so the two surfaces never drift out of sync.
//
// Composed manually (Dialog + DialogContent + Command, rather than the
// bundled CommandDialog helper) because the shadcn dialog.jsx's Tailwind
// `bg-background`/`bg-popover` CSS variables are still the default shadcn
// black/white palette in this project's index.css — ThemeContext never
// toggles a `.dark` class, it only sets inline styles — so the bundled
// helper would render with colors that don't match the app's actual
// light/dark theme. Composing manually lets us pass inline theme-token
// overrides straight through to DialogContent/Command, which both forward
// `style` onto their underlying Radix/cmdk primitives.
//
// OPEN_EVENT lets any component (e.g. Sidebar's visible Search button) open
// the palette without prop-drilling `open` through AppLayout or adding a new
// Context for one boolean — same low-ceremony pattern already used for
// --sidebar-width.
export const COMMAND_PALETTE_OPEN_EVENT = 'gammaqc:open-command-palette';

export default function CommandPalette() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const openHandler = () => setOpen(true);
    document.addEventListener('keydown', handler);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, openHandler);
    return () => {
      document.removeEventListener('keydown', handler);
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, openHandler);
    };
  }, []);

  const go = useCallback((item) => {
    setOpen(false);
    // External items are a full URL, not a router path — open in a new tab
    // rather than handing it to react-router's navigate().
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(item.path);
  }, [navigate]);

  const groups = NAV_FLAT.reduce((acc, item) => {
    (acc[item.group] = acc[item.group] || []).push(item);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="overflow-hidden p-0"
        style={{ background: theme.cardBg, border: `1px solid ${theme.borderColor}`, color: theme.textPrimary, maxWidth: 560 }}
      >
        <Command
          style={{ background: 'transparent', color: theme.textPrimary }}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-4 [&_[cmdk-input-wrapper]_svg]:w-4 [&_[cmdk-input]]:h-11 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item]_svg]:h-4 [&_[cmdk-item]_svg]:w-4"
        >
          <div style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
            <CommandInput
              placeholder="Jump to a feature…"
              style={{ color: theme.textPrimary }}
            />
          </div>
          <CommandList>
            <CommandEmpty style={{ color: theme.textSecondary }}>No matching feature.</CommandEmpty>
            {Object.entries(groups).map(([groupName, items]) => (
              <CommandGroup key={groupName} heading={groupName} style={{ color: theme.textSecondary }}>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <CommandItem
                      key={item.path}
                      value={`${item.label} ${groupName}`}
                      onSelect={() => go(item)}
                      style={{ color: theme.textPrimary, cursor: 'pointer' }}
                    >
                      <Icon size={16} style={{ color: theme.primary }} />
                      <span>{item.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
